import React from 'react';
import PropTypes from 'prop-types';
import { InputNumber } from 'antd';
import style from './index.scss';
import useAlertLevelConfigs from '../../../common/hooks/useAlertLevelConfig';
import { bandErrors } from './scoringCopy';

// FIN-13794 (Q10 revised — 2026-09-30 product lock): the three-textbox MIN-oriented input.
// Admins now enter the START of each band ("Low", "Good", "High" = LowMin, GoodMin, HighMin).
// The DTO storage shape is unchanged (FQ3 backward-compat: `{lowMin, lowMax, mediumMax}`); the
// component translates on the way in and out:
//   Display   ↔  Storage
//   Low  MIN  = lowMin (0 default)
//   Good MIN  = lowMax + 1
//   High MIN  = mediumMax + 1
// Runtime evaluates `<` on the derived MINs (see AlertLevelThresholds), so displayed MINs match
// the admin's mental model 1:1. Strict-less-than invariant per Q13 / adversarial P2-V3:
//   0 <= lowMin < goodMin < highMin < 101  (equivalent to  0 <= lowMin < lowMax < mediumMax < 100)

const COLORS = { low: '#52c41a', medium: '#faad14', high: '#f5222d' };

const BandTag = ({ tone, color, label }) => (
  <span className={`${style['band-tag']} ${style[`band-tag-${tone}`]}`}>
    <span className={style['band-tag-dot']} style={{ background: color }} />
    <span className={style['band-tag-label']}>{label}</span>
  </span>
);
BandTag.propTypes = {
  tone: PropTypes.oneOf(['low', 'medium', 'high']).isRequired,
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
};

const AlertLevelBandSlider = ({
  lowMin,
  lowMax,
  mediumMax,
  onChange,
  disabled,
  alertLevels
}) => {
  const { getLevelLabel, getLevelColor } = useAlertLevelConfigs(alertLevels);
  const lowName = getLevelLabel('Low');
  const goodName = getLevelLabel('Good');
  const highName = getLevelLabel('High');
  const lowColor = getLevelColor('Low') || COLORS.low;
  const goodColor = getLevelColor('Good') || COLORS.medium;
  const highColor = getLevelColor('High') || COLORS.high;

  // Displayed MINs derived from the storage shape.
  const lowMinDisplay = typeof lowMin === 'number' ? lowMin : 0;
  const goodMinDisplay = lowMax + 1;
  const highMinDisplay = mediumMax + 1;

  const errors = bandErrors(
    { lowMin: lowMinDisplay, lowMax, mediumMax },
    { low: lowName, good: goodName, high: highName }
  );

  // Storage translation: LowMin stays; GoodMin -> lowMax = goodMin - 1; HighMin -> mediumMax = highMin - 1.
  const emit = ({
    lowMin: nextLowMin = lowMinDisplay,
    goodMin: nextGoodMin = goodMinDisplay,
    highMin: nextHighMin = highMinDisplay
  }) =>
    onChange({
      lowMin: nextLowMin,
      lowMax: nextGoodMin - 1,
      mediumMax: nextHighMin - 1
    });

  return (
    <div className={style['alert-level-band-slider']}>
      <div className={style['band-tags']}>
        <BandTag
          tone="low"
          color={lowColor}
          label={`${lowName}: ${lowMinDisplay} – ${lowMax}`}
        />
        <BandTag
          tone="medium"
          color={goodColor}
          label={`${goodName}: ${goodMinDisplay} – ${mediumMax}`}
        />
        <BandTag
          tone="high"
          color={highColor}
          label={`${highName}: ${highMinDisplay} – 100`}
        />
      </div>

      <div className={style['band-rail-wrap']}>
        <div className={style['band-rail']}>
          <div
            className={style['band-rail-segment']}
            style={{ width: `${lowMinDisplay}%`, background: '#e0e0e0' }}
            title={`Suppressed (below ${lowName})`}
          />
          <div
            className={style['band-rail-segment']}
            style={{
              width: `${Math.max(0, lowMax - lowMinDisplay)}%`,
              background: lowColor
            }}
          />
          <div
            className={style['band-rail-segment']}
            style={{
              width: `${Math.max(0, mediumMax - lowMax)}%`,
              background: goodColor
            }}
          />
          <div
            className={style['band-rail-segment']}
            style={{
              width: `${Math.max(0, 100 - mediumMax)}%`,
              background: highColor
            }}
          />
        </div>
      </div>

      <div className={style['band-inputs']}>
        <label htmlFor="band-low-min" className={style['band-input-label']}>
          <span className={style['band-input-text']}>{lowName} ≥</span>
          <InputNumber
            id="band-low-min"
            aria-label={`${lowName} starts at`}
            data-testid="band-low-min"
            min={0}
            max={100}
            step={1}
            precision={0}
            value={lowMinDisplay}
            onChange={(v) => typeof v === 'number' && emit({ lowMin: v })}
            disabled={disabled}
            size="small"
            className={style['band-input-number']}
          />
        </label>
        <label htmlFor="band-good-min" className={style['band-input-label']}>
          <span className={style['band-input-text']}>{goodName} ≥</span>
          <InputNumber
            id="band-good-min"
            aria-label={`${goodName} starts at`}
            data-testid="band-good-min"
            min={1}
            max={100}
            step={1}
            precision={0}
            value={goodMinDisplay}
            onChange={(v) => typeof v === 'number' && emit({ goodMin: v })}
            disabled={disabled}
            size="small"
            className={style['band-input-number']}
          />
        </label>
        <label htmlFor="band-high-min" className={style['band-input-label']}>
          <span className={style['band-input-text']}>{highName} ≥</span>
          <InputNumber
            id="band-high-min"
            aria-label={`${highName} starts at`}
            data-testid="band-high-min"
            min={2}
            max={100}
            step={1}
            precision={0}
            value={highMinDisplay}
            onChange={(v) => typeof v === 'number' && emit({ highMin: v })}
            disabled={disabled}
            size="small"
            className={style['band-input-number']}
          />
        </label>
      </div>

      {errors.length > 0 && (
        <div role="alert" className={style['band-error']}>
          {errors.join(' ')}
        </div>
      )}
    </div>
  );
};

AlertLevelBandSlider.propTypes = {
  lowMin: PropTypes.number,
  lowMax: PropTypes.number.isRequired,
  mediumMax: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  alertLevels: PropTypes.array
};
AlertLevelBandSlider.defaultProps = {
  lowMin: 0,
  disabled: false,
  alertLevels: []
};

export default AlertLevelBandSlider;
